import React, { useMemo } from 'react';
import { ETH } from '../economics/ecoShared';
import type { DeclineDataPoint } from './declineCalculations';
import { buildSensitivityModelCurves, buildSensitivityModelResults } from './sensitivityModelCalculations';
import type { SensitivityDeclineModel } from './sensitivityModelCalculations';

export default function SensitivityModelComparison({ data, lastT, forecastEndT, terminalDeclinePct, selectedModel }: {
  data: DeclineDataPoint[];
  lastT: number;
  forecastEndT: number;
  terminalDeclinePct: number;
  selectedModel: SensitivityDeclineModel;
}) {
  const results = useMemo(() => buildSensitivityModelResults(data, lastT, forecastEndT, terminalDeclinePct), [data, lastT, forecastEndT, terminalDeclinePct]);
  const curves = useMemo(() => buildSensitivityModelCurves(data, lastT, forecastEndT, terminalDeclinePct), [data, lastT, forecastEndT, terminalDeclinePct]);

  if (results.length === 0) return null;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ padding: 14, background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}` }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>多递减模型预测对比</div>
        <div style={{ marginTop: 4, fontSize: 10, color: ETH.textMuted }}>同一生产历史和预测期限下，对比不同递减模型的拟合质量、未来累计产气和预测末期日产气</div>

        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 850, borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>模型</th>
                <th style={thStyle}>R²</th>
                <th style={thStyle}>RMSE</th>
                <th style={thStyle}>未来累计产气（历史末点后）</th>
                <th style={thStyle}>预测末期日产气</th>
                <th style={thStyle}>当前选择</th>
              </tr>
            </thead>

            <tbody>
              {results.map((item) => (
                <tr key={item.key}>
                  <td style={tdStyle}>{item.label}</td>
                  <td style={numberTdStyle}>{item.r2.toFixed(4)}</td>
                  <td style={numberTdStyle}>{item.rmse.toFixed(4)}</td>
                  <td style={numberTdStyle}>{fmtScientific(item.futureCumulative * 1e4)} m³</td>
                  <td style={numberTdStyle}>{item.finalRate.toFixed(3)} ×10⁴m³/d</td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: item.key === selectedModel ? 800 : 400 }}>{item.key === selectedModel ? '●' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ModelCurveChart history={data} curves={curves} selectedModel={selectedModel} />
    </div>
  );
}

function ModelCurveChart({ history, curves, selectedModel }: {
  history: DeclineDataPoint[];
  curves: ReturnType<typeof buildSensitivityModelCurves>;
  selectedModel: SensitivityDeclineModel;
}) {
  const width = 920;
  const height = 390;
  const left = 65;
  const right = 30;
  const top = 35;
  const bottom = 55;
  const plotW = width - left - right;
  const plotH = height - top - bottom;

  const allT = [...history.map((p) => p.t), ...curves.flatMap((curve) => curve.points.map((p) => p.t))];
  const allQ = [...history.map((p) => p.q), ...curves.flatMap((curve) => curve.points.map((p) => p.q))];

  const maxT = Math.max(1, ...allT);
  const maxQ = Math.max(1, ...allQ) * 1.08;
  const x = (value: number) => left + value / maxT * plotW;
  const y = (value: number) => top + (1 - value / maxQ) * plotH;

  const colors: Record<SensitivityDeclineModel, string> = {
    exponential: '#b64f35',
    hyperbolic: '#3c6e91',
    harmonic: '#6a7d4f',
    sepd: '#8a5a9e',
    duong: '#b2873c',
    segmented: '#444444',
  };

  return (
    <div style={{ marginTop: 14, padding: 14, background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}` }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>多模型未来产量曲线</div>
      <div style={{ marginTop: 4, fontSize: 10, color: ETH.textMuted }}>黑色圆点为生产历史；彩色曲线为不同递减模型的未来预测</div>

      <div style={{ marginTop: 10, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {curves.map((curve) => (
          <div key={curve.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: ETH.textMuted, fontWeight: curve.key === selectedModel ? 800 : 400 }}>
            <span style={{ width: 20, height: 3, background: colors[curve.key], display: 'inline-block' }} />
            {curve.label}{curve.key === selectedModel ? '（当前）' : ''}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 8, overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', minWidth: 760, height }}>
          <rect width={width} height={height} fill={ETH.bg} />

          {Array.from({ length: 6 }, (_, i) => i).map((i) => {
            const q = maxQ * i / 5;
            return (
              <g key={`y-${i}`}>
                <line x1={left} x2={width - right} y1={y(q)} y2={y(q)} stroke={ETH.headerBorder} />
                <text x={left - 8} y={y(q) + 4} textAnchor="end" fontSize={10} fill={ETH.textMuted}>{q.toFixed(1)}</text>
              </g>
            );
          })}

          {Array.from({ length: 6 }, (_, i) => i).map((i) => {
            const t = maxT * i / 5;
            return (
              <g key={`x-${i}`}>
                <line x1={x(t)} x2={x(t)} y1={top} y2={height - bottom} stroke={ETH.headerBorder} />
                <text x={x(t)} y={height - bottom + 18} textAnchor="middle" fontSize={10} fill={ETH.textMuted}>{t.toFixed(0)}</text>
              </g>
            );
          })}

          {curves.map((curve) => {
            const d = curve.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(point.t)} ${y(point.q)}`).join(' ');
            return <path key={curve.key} d={d} fill="none" stroke={colors[curve.key]} strokeWidth={curve.key === selectedModel ? 3 : 1.7} opacity={curve.key === selectedModel ? 1 : 0.72} />;
          })}

          {history.map((point, index) => <circle key={`${point.date}-${index}`} cx={x(point.t)} cy={y(point.q)} r={4} fill={ETH.text} />)}

          <text x={left + plotW / 2} y={height - 10} textAnchor="middle" fontSize={11} fill={ETH.textSec}>生产时间 / d</text>
          <text x={16} y={top + plotH / 2} textAnchor="middle" fontSize={11} fill={ETH.textSec} transform={`rotate(-90 16 ${top + plotH / 2})`}>日产气 / 10⁴m³/d</text>
        </svg>
      </div>
    </div>
  );
}

function fmtScientific(value: number, digits = 3) {
  if (!Number.isFinite(value)) return '--';
  if (value === 0) return '0';

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const coefficient = value / Math.pow(10, exponent);
  return `${coefficient.toFixed(digits)} × 10${toSuperscript(exponent)}`;
}

function toSuperscript(value: number) {
  const map: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' };
  return String(value).split('').map((char) => map[char] ?? char).join('');
}

const thStyle: React.CSSProperties = { padding: '8px', borderBottom: `1px solid ${ETH.headerBorder}`, textAlign: 'center', color: ETH.textSec, whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '8px', borderBottom: `1px solid ${ETH.headerBorder}` };
const numberTdStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right', fontFamily: ETH.fontMono, whiteSpace: 'nowrap' };